/**
 * Settings hooks. Loads the key/value settings map from the API; falls back to an
 * empty map (callers then use their UI defaults) when anonymous or the API errors.
 * Upsert writes a single non-sensitive setting via PUT /settings/:key.
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SETTINGS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import { useAuthStore } from "@/features/auth/auth.store";
import { settingsApi } from "./settings.api";
import { toSettingsMap, type SettingsMap } from "./settings.adapter";

/** Stable empty map so consumers' effects don't re-fire each render. */
const EMPTY_MAP: SettingsMap = {};

export type DataSource = "api" | "mock";

export interface UseSettingsResult {
  settings: SettingsMap;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
}

export function useSettings(): UseSettingsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");

  const query = useQuery({
    queryKey: queryKeys.settingsList,
    queryFn: () => settingsApi.list(),
    enabled: authed,
  });

  const settings = useMemo<SettingsMap>(
    () => (query.data ? toSettingsMap(query.data.data) : EMPTY_MAP),
    [query.data],
  );

  if (!authed) return { settings: EMPTY_MAP, isLoading: false, isError: false, source: "mock", refetch: query.refetch };
  if (query.data) return { settings, isLoading: false, isError: false, source: "api", refetch: query.refetch };
  if (query.isError) return { settings: EMPTY_MAP, isLoading: false, isError: true, source: "mock", refetch: query.refetch };
  return { settings: EMPTY_MAP, isLoading: query.isLoading, isError: false, source: "api", refetch: query.refetch };
}

/** Upsert a single setting. In mock mode it is a no-op (returns null). */
export function useUpsertSetting() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      if (authed) return settingsApi.upsert(key, value);
      return null;
    },
    onSuccess: () => {
      if (authed) void qc.invalidateQueries({ queryKey: SETTINGS_QUERY_PREFIX });
    },
  });
}
